import React from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { View } from 'react-native';
import { ScreenContainer } from '../../../layout/ScreenContainer';
import { LineTitle } from '../../../molecules/LineTitle';
import { MediumText } from '../../../atoms/MediumText';
import ClientLogo from '../../../atoms/ClientLogo';

export const DDRSAScreen = (): React.ReactElement => {
  return (
    <ScrollView>
      <ScreenContainer>
        <LineTitle title="Zai Direct Debit Request Service Agreement" />

        {/* Section 1: Logo and Company Details */}
        <View
          style={{
            flexDirection: 'row',
            marginBottom: 24,
            alignItems: 'flex-start',
          }}
        >
          {/* Left side: PokitPal Logo */}
          <View style={{ flex: 1, height: 110 }}>
            <ClientLogo />
          </View>

          {/* Right side: Company Details */}
          <View style={{ flex: 2, paddingLeft: 16 }}>
            <MediumText style={{ fontWeight: 'bold', marginBottom: 4 }}>
              PokitPal Pty Ltd
            </MediumText>
            <MediumText style={{ marginBottom: 2 }}>
              Level 17, International Tower 3, 300 Barangaroo Ave
            </MediumText>
            <MediumText>+61 2 8089 4054</MediumText>
          </View>
        </View>

        {/* Service Agreement Content */}
        <MediumText style={{ marginBottom: 16, lineHeight: 20 }}>
          This is your Direct Debit Service Agreement with Zai Australia Pty Ltd
          (Zai), User ID's 342203 and 481561, ABN: 96 637 632 645, (the Debit
          User), as agent for PokitPal Pty Ltd (ABN: 49 131 686 167) to arrange
          a debit to your nominated account to pay for Round-Ups related goods
          and services provided by PokitPal.
        </MediumText>

        <MediumText style={{ marginBottom: 16, lineHeight: 20 }}>
          It explains what your obligations are when undertaking a Direct Debit
          arrangement with us. It also details what our obligations are to you
          as your Direct Debit provider.
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          Please keep this agreement for future reference. It forms part of the
          terms and conditions of your Direct Debit Request (DDR) and should be
          read in conjunction with your DDR authorisation.
        </MediumText>

        {/* Definitions */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          Definitions
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>account</MediumText> means
          the account held at your financial institution from which we are
          authorised to arrange for funds to be debited.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>agreement</MediumText>{' '}
          means this Direct Debit Request Service Agreement between you and us.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>banking day</MediumText>{' '}
          means a day other than a Saturday or a Sunday or a public holiday
          listed throughout Australia.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>debit day</MediumText>{' '}
          means the day that payment by you to us is due.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>debit payment</MediumText>{' '}
          means a particular transaction where a debit is made.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>
            direct debit request
          </MediumText>{' '}
          means the Direct Debit Request between us and you.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>us or we</MediumText> means
          Zai Australia Pty Ltd (the Debit User), acting on behalf of PokitPal
          whom you have authorised by requesting a Direct Debit Request.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>you</MediumText> means the
          customer who has signed or authorised by other means the Direct Debit
          Request.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>
            your financial institution
          </MediumText>{' '}
          means the financial institution nominated by you on the DDR at which
          the account is maintained.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>PokitPal</MediumText> means
          the party that provides the services you are paying for, by way of
          requesting us to process your valid Debit payment instructions
          (Software Application Provider/Platform).
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          <MediumText style={{ fontWeight: 'bold' }}>
            PokitPal (Platform)
          </MediumText>{' '}
          means the provider of the software application that collects valid
          instructions authorised by PokitPal and you. They provide these
          instructions to us to enable the processing of related debit payments
          instructions.
        </MediumText>

        {/* Section 1: Debiting your account */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          1. Debiting your account
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          1.1 By submitting a Direct Debit Request, you have authorised us to
          arrange for funds to be debited from your account. The Direct Debit
          Request and this agreement set out the terms of the arrangement
          between us and you.
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          1.2 We will only arrange for funds to be debited from your account as
          authorised in the Direct Debit Request.
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          1.3 If the debit day falls on a day that is not a banking day, we may
          direct your financial institution to debit your account on the
          following banking day. If you are unsure about which day your account
          has or will be debited you should ask your financial institution.
        </MediumText>

        {/* Section 2: Amendments by us */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          2. Amendments by us
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          2.1 We may vary any details of this agreement or a Direct Debit
          Request at any time by giving you at least 30 days written notice sent
          to the preferred email or address you have given us in the Direct
          Debit Request.
        </MediumText>

        {/* Section 3: Amendments by you */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          3. Amendments by you
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          3.1 You can at any time by giving us 5 days' notice:
        </MediumText>

        <MediumText
          style={{ marginBottom: 8, lineHeight: 20, paddingLeft: 16 }}
        >
          (a) cancel or suspend the Direct Debit Request; or
        </MediumText>

        <MediumText
          style={{ marginBottom: 12, lineHeight: 20, paddingLeft: 16 }}
        >
          (b) change, stop or defer an individual debit payment;
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          To do so, you can:
        </MediumText>

        <MediumText
          style={{ marginBottom: 8, lineHeight: 20, paddingLeft: 16 }}
        >
          (i) update your instructions using the PokitPal software application.
          PokitPal will notify us and we will act on your instructions; OR
        </MediumText>

        <MediumText
          style={{ marginBottom: 12, lineHeight: 20, paddingLeft: 16 }}
        >
          (ii) to cancel your Direct Debit Request, notify us directly via email
          to: helpdesk+pokitpal@hellozai.com or by mail to: Zai Australia Pty
          Ltd, Attention: PokitPal Support Team, The Commons, Level 1, 55
          Collins Street, Melbourne, VIC 3000.
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          You can also contact your own financial institution, which must act
          promptly on your instructions.
        </MediumText>

        {/* Section 4: Your obligations */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          4. Your obligations
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          4.1 It is your responsibility to ensure that there are sufficient
          clear funds available in your account to allow a debit payment to be
          made in accordance with the Direct Debit Request.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          4.2 If there are insufficient clear funds in your account to meet a
          debit payment:
        </MediumText>

        <MediumText
          style={{ marginBottom: 6, lineHeight: 20, paddingLeft: 16 }}
        >
          (a) you may be charged a fee and/or interest by your financial
          institution;
        </MediumText>

        <MediumText
          style={{ marginBottom: 6, lineHeight: 20, paddingLeft: 16 }}
        >
          (b) we may charge you reasonable costs incurred by us on account of
          there being insufficient funds; and
        </MediumText>

        <MediumText
          style={{ marginBottom: 12, lineHeight: 20, paddingLeft: 16 }}
        >
          (c) you must arrange for the debit payment to be made by another
          method or arrange for sufficient clear funds to be in your account by
          an agreed time so that we can process the debit payment.
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          4.3 You should check your account statement to verify that the amounts
          debited from your account are correct.
        </MediumText>

        {/* Section 5: Dispute */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          5. Dispute
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          5.1 If you believe that there has been an error in debiting your
          account, you should notify us directly at
          helpdesk+pokitpal@hellozai.com or by mail to: Zai Australia Pty Ltd,
          Attention: PokitPal Support Team, The Commons, Level 1, 55 Collins
          Street, Melbourne VIC 3000. Alternatively, you can contact your
          financial institution and/or PokitPal assistance.
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          5.2 If we conclude as a result of our investigations that your account
          has been incorrectly debited we will respond to your query by
          arranging within a reasonable period, for your financial institution
          to adjust your account (including interest and charges) accordingly.
          We will also notify you in writing of the amount by which your account
          has been adjusted.
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          5.3 If we conclude as a result of our investigations that your account
          has not been incorrectly debited we will respond to your query by
          providing you with reasons and any evidence for this finding in
          writing.
        </MediumText>

        {/* Section 6: Accounts */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          6. Accounts
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          6.1 You should check:
        </MediumText>

        <MediumText
          style={{ marginBottom: 6, lineHeight: 20, paddingLeft: 16 }}
        >
          (a) with your financial institution whether direct debiting is
          available from your account as direct debiting is not available on all
          accounts offered by financial institutions.
        </MediumText>

        <MediumText
          style={{ marginBottom: 6, lineHeight: 20, paddingLeft: 16 }}
        >
          (b) your account details which you have provided to us are correct by
          checking them against a recent account statement; and
        </MediumText>

        <MediumText
          style={{ marginBottom: 12, lineHeight: 20, paddingLeft: 16 }}
        >
          (c) with your financial institution before completing the Direct Debit
          Request if you have any queries about how to complete the Direct Debit
          Request.
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          6.2 While the account you nominate for this purpose may be jointly
          held by more parties than those signing the Direct Debit Request, you
          must nominate an account that your Financial Institution permits you
          to operate including the permission to authorise the debiting of that
          account, without your Financial Institution requiring additional party
          to also authorise such debit payments.
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          6.3 If you have identified yourself as an individual only in the
          Direct Debit Request, for the purposes of the Direct Debit Request,
          you may not nominate an Account issued to or operated by a company or
          another similar type of entity structure, such as a Trust.
        </MediumText>

        {/* Section 7: Confidentiality */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          7. Confidentiality
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          7.1 We will keep any information (including your account details) in
          your Direct Debit Request confidential. We will make reasonable
          efforts to keep any such information that we have about you secure and
          to ensure that any of our employees or agents who have access to
          information about you do not make any unauthorised use, modification,
          reproduction or disclosure of that information.
        </MediumText>

        <MediumText style={{ marginBottom: 8, lineHeight: 20 }}>
          7.2 We will only disclose information that we have about you:
        </MediumText>

        <MediumText
          style={{ marginBottom: 6, lineHeight: 20, paddingLeft: 16 }}
        >
          (a) to the extent specifically required by law; or
        </MediumText>

        <MediumText
          style={{ marginBottom: 24, lineHeight: 20, paddingLeft: 16 }}
        >
          (b) for the purposes of this agreement (including disclosing
          information in connection with any query or claim).
        </MediumText>

        {/* Section 8: Notice */}
        <MediumText
          style={{ fontWeight: 'bold', marginBottom: 12, fontSize: 16 }}
        >
          8. Notice
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          8.1 If you wish to notify us in writing about anything relating to
          this agreement, you should email us at helpdesk+pokitpal@hellozai.com
          or mail us at: Zai Australia Pty Ltd, Attention: PokitPal Support
          Team, The Commons, Level 1, 55 Collins Street, Melbourne VIC 3000.
        </MediumText>

        <MediumText style={{ marginBottom: 12, lineHeight: 20 }}>
          8.2 We will notify you by sending a notice to the preferred contact
          method you have given us in the Direct Debit Request.
        </MediumText>

        <MediumText style={{ marginBottom: 24, lineHeight: 20 }}>
          8.3 Any notice will be deemed to have been received on the second
          banking day after sending if sent via email, or on the fifth banking
          day if sent via post.
        </MediumText>
      </ScreenContainer>
    </ScrollView>
  );
};
